import { useState, useEffect, useMemo, useCallback } from 'react';
import { useUrlParam } from '../hooks/useUrlParam';
import {
  FeedbackBatchActions, FeedbackBatchDetail, FeedbackMergeBar, FeedbackPickBox, FeedbackSelectToggle, FeedbackThumb, FeedbackTypeChip, FeedbackWhere,
} from '../components/comms/FeedbackFeed';
import { useFeedbackFeed } from '../components/comms/useFeedbackFeed';
import { feedbackAt, feedbackMatches, feedbackTab, type FeedbackBatch } from '../lib/clientFeedback';
import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import Sidebar from '../components/layout/Sidebar';
import PageHeader from '../components/PageHeader';
import {
  Button,
  Badge,
  StatCard,
  Tabs,
  Table,
  Dropdown,
  Icon,
  Input,
  SearchableSelect,
  SidePanel,
  Switch,
} from '../components/ui';
import type { TableColumn, BadgeTone } from '../components/ui';
import { useUser } from '../context/UserContext';
import { supabase } from '../lib/supabase';
import { uniqueChannelName } from '../lib/realtime';
import { formatForDateInput, getCurrentDate } from '../utils/dateUtils';
import BoardExplorerPanel from '../components/comms/BoardExplorerPanel';
import TrelloCardDetailBody from '../components/comms/TrelloCardDetailBody';
import useMediaQuery from '../components/clients/useMediaQuery';
import TaskDetailSidepanel from '../components/clientDetail/sections/functions/TaskDetailSidepanel';
import type { TaskDetailTask } from '../components/clientDetail/sections/functions/TaskDetailSidepanel';
import { updateTask } from '../components/clientDetail/sections/functions/TasksUtils';
import {
  CommsItem,
  CommsKind,
  CommsBoard,
  CommsSyncState,
  CommsView,
  CardGroup,
  CardTaskInfo,
  KIND_LABELS,
  groupItemsByCard,
  isTaskActive,
  timeAgo,
  age,
  excerpt,
} from '../lib/comms';

// Disconnected once this many ms pass without a sync run (3 cron ticks)
const SYNC_STALE_MS = 15 * 60 * 1000;

const TAB_LABELS: Array<{ label: string; value: CommsView }> = [
  { label: 'Open', value: 'open' },
  { label: 'Assigned', value: 'assigned' },
  { label: 'Cleared', value: 'cleared' },
];
// One feed: Trello cards, escalated emails, and client feedback batches (feedback
// mode in apps we built), newest first, each typed by its chip.
type FeedRow =
  | { type: 'comms'; key: string; at: string; group: CardGroup }
  | { type: 'feedback'; key: string; at: string; batch: FeedbackBatch };

const KIND_TONES: Record<CommsKind, BadgeTone> = {
  email: 'info',
  card_created: 'accent',
  comment: 'info',
  description_changed: 'warn',
  card_moved: 'up',
  card_renamed: 'neutral',
  attachment_added: 'idle',
  manual_import: 'info',
};

const normalizeRole = (role?: string | null) => role?.trim?.().toLowerCase?.();

// Escapes the Badge's uppercase so "3m" doesn't read as "3M" (three months)
const noTransform = { textTransform: 'none' } as const;

export default function ClientComms() {
  const { user, isUserLoaded } = useUser();
  const [items, setItems] = useState<CommsItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  // The tab (?view=) and the open conversation (?card=) live in the URL, so refresh and back keep your place.
  const [viewParam, setViewParam] = useUrlParam('view');
  // The old Comms → Feedback screen (?view=feedback) now lives in the Open feed; its links land there.
  useEffect(() => { if (viewParam === 'feedback') setViewParam(null); }, [viewParam, setViewParam]);
  const activeTab = (viewParam === 'feedback' ? 'open' : viewParam ?? 'open') as CommsView;
  // An open feedback batch (?fb=<batch id>), the way ?card= holds a conversation.
  const [selectedFeedbackId, setSelectedFeedbackId] = useUrlParam('fb');
  const setActiveTab = (view: CommsView) => setViewParam(view === 'open' ? null : view);
  const [tasksByCard, setTasksByCard] = useState<Map<string, CardTaskInfo>>(new Map());
  const [isSyncing, setIsSyncing] = useState(false);
  const [selectedCardId, setSelectedCardId] = useUrlParam('card');
  const [boards, setBoards] = useState<CommsBoard[]>([]);
  const [isBoardsOpen, setIsBoardsOpen] = useState(false);
  const [isExplorerOpen, setIsExplorerOpen] = useState(false);
  const [syncState, setSyncState] = useState<CommsSyncState | null>(null);
  const [nowTick, setNowTick] = useState(() => Date.now());
  const [linkedTask, setLinkedTask] = useState<{ id: string; due_date: string | null; status: string | null; assigned_to_name?: string | null } | null>(null);
  const [taskDueDate, setTaskDueDate] = useState(() => formatForDateInput(getCurrentDate()));
  const [taskAssignee, setTaskAssignee] = useState<string | null>(null);
  const [assignees, setAssignees] = useState<Array<{ id: string; name: string; email: string }>>([]);
  // Email items have no board, so their client is attached by hand here.
  const [clients, setClients] = useState<Array<{ id: string; name: string }>>([]);
  const [savingClient, setSavingClient] = useState(false);
  const [isCreatingTask, setIsCreatingTask] = useState(false);
  const [detailTask, setDetailTask] = useState<TaskDetailTask | null>(null);
  const [isTaskDetailOpen, setIsTaskDetailOpen] = useState(false);
  const [isAttachOpen, setIsAttachOpen] = useState(false);
  const [attachTaskId, setAttachTaskId] = useState<string | null>(null);
  const [attachError, setAttachError] = useState<string | null>(null);
  const [attachableTasks, setAttachableTasks] = useState<Array<{ id: string; title: string; status: string; assigned_to_name: string | null; due_date: string | null; client: { name: string } | null }>>([]);
  const [isAttaching, setIsAttaching] = useState(false);

  const userRole = normalizeRole(user?.role);
  const hasAccess = userRole === 'admin' || userRole === 'developer' || userRole === 'agency_owner';
  const feed = useFeedbackFeed(isUserLoaded && hasAccess);
  // The feedback panel carries screenshots and threads: wider where there's room.
  const widePanel = useMediaQuery('(min-width: 1440px)');

  const fetchItems = useCallback(async () => {
    try {
      // Two queries, not one. Trello rows must keep the !inner join so items
      // from de-activated boards drop out. Email rows have no board at all, so
      // that same join would silently delete them — and a single left-joined
      // query with a 1000 row limit would let inactive-board rows eat the
      // budget before the filter ran.
      const [trelloRes, emailRes] = await Promise.all([
        supabase
          .from('comms_items')
          .select('*, comms_boards!inner(board_name, client_id)')
          .eq('comms_boards.is_active', true)
          .eq('source', 'trello')
          .order('occurred_at', { ascending: false })
          .limit(1000),
        supabase
          .from('comms_items')
          .select('*')
          .eq('source', 'email')
          .order('occurred_at', { ascending: false })
          .limit(500),
      ]);

      if (trelloRes.error) throw trelloRes.error;
      if (emailRes.error) throw emailRes.error;

      const merged = [
        ...((trelloRes.data as CommsItem[]) || []),
        ...((emailRes.data as CommsItem[]) || []),
      ].sort(
        (a, b) => new Date(b.occurred_at).getTime() - new Date(a.occurred_at).getTime()
      );
      setItems(merged);

      // Linked tasks decide which cards are "Assigned"
      const { data: linkRows, error: linksError } = await supabase
        .from('comms_task_links')
        .select('card_id, task:client_tasks(id, status, archived, assigned_to_name, due_date)');
      if (linksError) throw linksError;
      const map = new Map<string, CardTaskInfo>();
      ((linkRows as unknown as Array<{ card_id: string; task: CardTaskInfo | null }>) || []).forEach(l => {
        if (l.task) map.set(l.card_id, l.task);
      });
      setTasksByCard(map);

      setLoadError(null);
    } catch (error) {
      console.error('Error fetching client comms:', error);
      setLoadError('Could not load client comms. Try Sync Now — if it keeps failing, check the console for details.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchBoards = useCallback(async () => {
    const { data, error } = await supabase
      .from('comms_boards')
      .select('id, trello_board_id, board_name, client_id, is_active, last_synced_action_at')
      .order('board_name', { ascending: true });
    if (!error) setBoards((data as CommsBoard[]) || []);
  }, []);

  const fetchSyncState = useCallback(async () => {
    const { data, error } = await supabase
      .from('comms_sync_state')
      .select('*')
      .maybeSingle();
    if (!error) setSyncState((data as unknown as CommsSyncState | null) ?? null);
  }, []);

  const fetchAssignees = useCallback(async () => {
    const { data, error } = await supabase
      .from('users')
      .select('id, name, email')
      .order('name');
    if (!error) setAssignees((data as Array<{ id: string; name: string; email: string }>) || []);

    const { data: clientRows } = await supabase
      .from('clients')
      .select('id, name')
      .order('name');
    setClients((clientRows as Array<{ id: string; name: string }>) || []);
  }, []);

  useEffect(() => {
    // Wait for isUserLoaded: it flips true only after the Supabase session is
    // restored, so fetching earlier silently returns zero rows under RLS.
    if (isUserLoaded && hasAccess) {
      fetchItems();
      fetchBoards();
      fetchSyncState();
      fetchAssignees();
    }
  }, [isUserLoaded, hasAccess, fetchItems, fetchBoards, fetchSyncState, fetchAssignees]);

  // Re-render every minute so "Synced Xm ago" and staleness stay honest
  useEffect(() => {
    const timer = setInterval(() => setNowTick(Date.now()), 60 * 1000);
    return () => clearInterval(timer);
  }, []);

  // Live updates when the sync lands new items or someone else clears a card
  useEffect(() => {
    if (!isUserLoaded || !hasAccess) return;

    const channel = supabase
      .channel(uniqueChannelName('comms_items_channel'))
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'comms_items' },
        fetchItems
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'comms_sync_state' },
        fetchSyncState
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'client_tasks' },
        fetchItems // task created/submitted/archived moves cards between tabs
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'comms_task_links' },
        fetchItems // attach/detach moves cards between tabs
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [isUserLoaded, hasAccess, fetchItems, fetchSyncState]);

  const handleToggleBoard = async (board: CommsBoard, isActive: boolean) => {
    // Optimistic flip so the switch doesn't lag behind the click
    setBoards(prev => prev.map(b => (b.id === board.id ? { ...b, is_active: isActive } : b)));
    const { error } = await supabase
      .from('comms_boards')
      .update({ is_active: isActive })
      .eq('id', board.id);
    if (error) {
      console.error('Error toggling board:', error);
      setBoards(prev => prev.map(b => (b.id === board.id ? { ...b, is_active: !isActive } : b)));
      return;
    }
    await fetchItems();
  };

  const handleSyncNow = async () => {
    setIsSyncing(true);
    try {
      const { error } = await supabase.functions.invoke('trello-sync', { body: {} });
      if (error) throw error;
    } catch (error) {
      console.error('Error running trello-sync:', error);
    }
    await fetchItems();
    await fetchSyncState();
    setIsSyncing(false);
  };

  const handleClearCard = async (group: CardGroup) => {
    const { error } = await supabase
      .from('comms_items')
      .update({
        status: 'cleared',
        cleared_by: user?.id ?? null,
        cleared_at: new Date().toISOString(),
        cleared_via: 'manual',
      })
      .eq('card_id', group.cardId)
      .eq('status', 'open');
    if (!error) {
      setSelectedCardId(null);
      await fetchItems();
    }
  };

  const handleReopenCard = async (group: CardGroup) => {
    const { error } = await supabase
      .from('comms_items')
      .update({ status: 'open', cleared_by: null, cleared_at: null, cleared_via: null })
      .eq('card_id', group.cardId)
      .eq('status', 'cleared')
      .eq('direction', 'inbound'); // team replies are display-only, never "open"
    if (!error) {
      setSelectedCardId(null);
      await fetchItems();
    }
  };

  const tabGroups = useMemo(
    () => ({
      open: groupItemsByCard(items, tasksByCard, 'open'),
      assigned: groupItemsByCard(items, tasksByCard, 'assigned'),
      cleared: groupItemsByCard(items, tasksByCard, 'cleared'),
    }),
    [items, tasksByCard]
  );
  const groups = tabGroups[activeTab];
  const feedbackByTab = useMemo(() => {
    const by: Record<CommsView, FeedbackBatch[]> = { open: [], assigned: [], cleared: [] };
    feed.batches.forEach(b => by[feedbackTab(b)].push(b));
    return by;
  }, [feed.batches]);

  const filteredRows = useMemo<FeedRow[]>(() => {
    const query = searchQuery.toLowerCase();
    const cards = !searchQuery
      ? groups
      : groups.filter(
          g =>
            g.cardName.toLowerCase().includes(query) ||
            g.boardName.toLowerCase().includes(query) ||
            g.items.some(
              i =>
                i.actor_name.toLowerCase().includes(query) ||
                i.body?.toLowerCase().includes(query)
            )
        );
    const batches = feedbackByTab[activeTab].filter(b => !searchQuery || feedbackMatches(b, query));
    return [
      ...cards.map(g => ({ type: 'comms' as const, key: `c:${g.cardId}`, at: g.latest.occurred_at, group: g })),
      ...batches.map(b => ({ type: 'feedback' as const, key: `f:${b.id}`, at: feedbackAt(b), batch: b })),
    ].sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
  }, [groups, feedbackByTab, activeTab, searchQuery]);

  const selectedFeedback = useMemo(
    () => (selectedFeedbackId ? feed.batches.find(b => b.id === selectedFeedbackId) ?? null : null),
    [feed.batches, selectedFeedbackId]
  );
  const openRow = (row: FeedRow) => {
    if (row.type === 'feedback') { setSelectedCardId(null); setSelectedFeedbackId(row.batch.id); }
    else { setSelectedFeedbackId(null); setSelectedCardId(row.group.cardId); }
  };

  // Search ALL tabs, not just the active one: attaching/clearing moves the
  // card between tabs and the open conversation panel must follow it, not
  // vanish mid-action.
  const selectedGroup = useMemo(() => {
    if (!selectedCardId) return null;
    return (
      tabGroups.open.find(g => g.cardId === selectedCardId) ??
      tabGroups.assigned.find(g => g.cardId === selectedCardId) ??
      tabGroups.cleared.find(g => g.cardId === selectedCardId) ??
      null
    );
  }, [tabGroups, selectedCardId]);

  // Does a task govern the selected card? (Archived-task links count as no
  // link: the card is back in play, and attach/create upsert over the row.)
  const refreshLinkedTask = useCallback(async (cardId: string) => {
    const { data, error } = await supabase
      .from('comms_task_links')
      .select('task:client_tasks(id, due_date, status, assigned_to_name, archived)')
      .eq('card_id', cardId)
      .maybeSingle();
    const task = (data as unknown as { task: { id: string; due_date: string | null; status: string | null; assigned_to_name?: string | null; archived?: boolean } | null } | null)?.task;
    setLinkedTask(!error && task && !task.archived ? task : null);
  }, []);

  useEffect(() => {
    setLinkedTask(null);
    setTaskDueDate(formatForDateInput(getCurrentDate()));
    setTaskAssignee(null);
    if (selectedCardId) refreshLinkedTask(selectedCardId);
  }, [selectedCardId, refreshLinkedTask]);

  const openLinkedTaskDetail = async () => {
    if (!linkedTask) return;
    const { data, error } = await supabase
      .from('client_tasks')
      .select('*')
      .eq('id', linkedTask.id)
      .single();
    if (!error && data) {
      setDetailTask(data as unknown as TaskDetailTask);
      setIsTaskDetailOpen(true);
    }
  };

  const handleTaskDetailUpdate = async (
    taskId: string,
    updated: { title: string; status: string; dueDate: string | null; assignedTo: string | null }
  ) => {
    const { success, error } = await updateTask(taskId, {
      title: updated.title,
      status: updated.status,
      dueDate: updated.dueDate,
      assignedTo: updated.assignedTo,
    });
    if (!success) {
      console.error('Error updating task from comms:', error);
      // Reject so the autosaving drawer reverts the field and toasts.
      throw new Error(error || 'Failed to update task');
    }
    // The drawer stays open; it autosaves per field.
    await fetchItems();
    if (selectedCardId) await refreshLinkedTask(selectedCardId);
  };

  const handleTaskDetailArchive = async (taskId: string) => {
    const { error } = await supabase
      .from('client_tasks')
      .update({ archived: true, archived_at: new Date().toISOString() })
      .eq('id', taskId);
    if (!error) {
      setIsTaskDetailOpen(false);
      await fetchItems();
      if (selectedCardId) await refreshLinkedTask(selectedCardId);
    }
  };

  const handleCreateTask = async (group: CardGroup) => {
    if (!group.clientId || !taskDueDate || !taskAssignee) return;
    setIsCreatingTask(true);
    try {
      // Anchor to noon local so the date doesn't shift a day across timezones
      const [y, m, d] = taskDueDate.split('-').map(Number);
      const due = new Date(y, m - 1, d, 12).toISOString();
      const latestInbound = group.items.find(i => i.direction === 'inbound');

      const { data, error } = await supabase
        .from('client_tasks')
        .insert({
          client_id: group.clientId,
          title: group.cardName,
          description: `Created from Client Comms.\nTrello card: ${group.cardUrl}${
            latestInbound?.body ? `\n\nLatest client activity (${latestInbound.actor_name}): ${latestInbound.body}` : ''
          }`,
          status: 'Scheduled',
          due_date: due,
          assigned_by: user?.id ?? null,
          assigned_to: taskAssignee,
          assigned_to_name: assignees.find(a => a.id === taskAssignee)?.name ?? null,
        })
        .select('id, due_date, status, assigned_to_name')
        .single();
      if (error) throw error;

      // Upsert so a stale link to an archived task gets replaced
      const task = data as { id: string; due_date: string | null; status: string | null; assigned_to_name?: string | null };
      const { error: linkError } = await supabase
        .from('comms_task_links')
        .upsert({ card_id: group.cardId, task_id: task.id }, { onConflict: 'card_id' });
      if (linkError) throw linkError;

      setLinkedTask(task);
      await fetchItems();
    } catch (error) {
      console.error('Error creating task from comms event:', error);
    } finally {
      setIsCreatingTask(false);
    }
  };

  const handleAttachToTask = async (group: CardGroup, taskId: string) => {
    setIsAttaching(true);
    setAttachError(null);
    try {
      const { error: linkError } = await supabase
        .from('comms_task_links')
        .upsert({ card_id: group.cardId, task_id: taskId }, { onConflict: 'card_id' });
      if (linkError) throw linkError;
      setIsAttachOpen(false);
      setAttachTaskId(null);
      await refreshLinkedTask(group.cardId);
      await fetchItems();
    } catch (error) {
      console.error('Error attaching comms event to task:', error);
      setAttachError('Could not attach — the link was rejected. Try again, and check the console if it persists.');
    } finally {
      setIsAttaching(false);
    }
  };

  const handleDetach = async (group: CardGroup) => {
    const { error: detachError } = await supabase
      .from('comms_task_links')
      .delete()
      .eq('card_id', group.cardId);
    if (!detachError) {
      await refreshLinkedTask(group.cardId);
      await fetchItems();
    }
  };

  /**
   * Attach (or detach) a client on an email-sourced card.
   *
   * Written to every comms_items row sharing this card_id, so a later message
   * in the same thread inherits it rather than arriving clientless.
   */
  const handleAttachClient = async (group: CardGroup, clientId: string | null) => {
    setSavingClient(true);
    try {
      const { error } = await supabase
        .from('comms_items')
        .update({ client_id: clientId })
        .eq('card_id', group.cardId)
        .eq('source', 'email');
      if (error) throw error;
      await fetchItems();
    } catch (err) {
      console.error('Error attaching client:', err);
    } finally {
      setSavingClient(false);
    }
  };

  const openAttachModal = async (group: CardGroup) => {
    setIsAttachOpen(true);
    setAttachTaskId(null);
    setAttachError(null);
    // Active tasks only — attaching to a Submitted task would instantly
    // clear the card; scope to the board's client when we know it
    let q = supabase
      .from('client_tasks')
      .select('id, title, status, assigned_to_name, due_date, client:clients(name)')
      .eq('archived', false)
      .not('status', 'in', '("Submitted")')
      .order('created_at', { ascending: false })
      .limit(200);
    if (group.clientId) q = q.eq('client_id', group.clientId);
    const { data, error: tasksError } = await q;
    if (!tasksError) {
      setAttachableTasks(
        ((data as unknown as Array<{ id: string; title: string; status: string; assigned_to_name: string | null; due_date: string | null; client: { name: string } | null }>) || [])
      );
    }
  };

  const stats = useMemo(() => {
    const openGroups = tabGroups.open;
    const openCards = openGroups.length + feedbackByTab.open.length;
    const assigned = tabGroups.assigned.length + feedbackByTab.assigned.length;
    const comments = openGroups.reduce((sum, g) => sum + g.counts.comment, 0);
    const openDates = openGroups.flatMap(g =>
      g.items.filter(i => i.direction === 'inbound' && i.status === 'open').map(i => i.occurred_at)
    );
    openDates.push(...feedbackByTab.open.map(b => b.received_at));
    const oldest = openDates.length > 0 ? openDates.reduce((min, d) => (d < min ? d : min)) : null;
    return { openCards, assigned, comments, oldest };
  }, [tabGroups, feedbackByTab]);

  const activeTabLabel = TAB_LABELS.find(t => t.value === activeTab)?.label ?? 'Open';

  // Connection indicator: error from the sync, or no run in 3 cron ticks,
  // means disconnected. nowTick keeps staleness honest without a reload.
  const connection = useMemo(() => {
    if (!syncState?.last_run_at) {
      return {
        tone: 'idle' as BadgeTone,
        pulse: false,
        label: 'Not connected' as ReactNode,
        title: 'The Trello sync has never run',
      };
    }
    const ageMs = nowTick - new Date(syncState.last_run_at).getTime();
    if (syncState.status === 'error') {
      return {
        tone: 'down' as BadgeTone,
        pulse: true,
        label: 'Disconnected' as ReactNode,
        title: syncState.error_detail || 'The last Trello sync failed',
      };
    }
    if (ageMs > SYNC_STALE_MS) {
      return {
        tone: 'down' as BadgeTone,
        pulse: true,
        label: (
          <>Sync stalled · <span style={noTransform}>{age(syncState.last_run_at)}</span> ago</>
        ) as ReactNode,
        title: 'The Trello sync has not run recently — check the cron job',
      };
    }
    return {
      tone: 'up' as BadgeTone,
      pulse: false,
      label: (
        <>Synced <span style={noTransform}>{age(syncState.last_success_at || syncState.last_run_at)}</span> ago</>
      ) as ReactNode,
      title: 'Trello connection healthy',
    };
  }, [syncState, nowTick]);

  // Open-card count per board id, for the Boards panel
  const openCardsByBoard = useMemo(() => {
    const map = new Map<string, Set<string>>();
    items.forEach(i => {
      if (i.status !== 'open') return;
      const existing = map.get(i.board_id) || new Set<string>();
      existing.add(i.card_id);
      map.set(i.board_id, existing);
    });
    return map;
  }, [items]);

  const kindSummary = (group: CardGroup) => {
    const parts: Array<{ kind: CommsKind; label: string }> = [];
    (Object.keys(KIND_LABELS) as CommsKind[]).forEach(kind => {
      const count = group.counts[kind];
      if (!count) return;
      let label: string = KIND_LABELS[kind];
      if (kind === 'comment') label = count === 1 ? '1 comment' : `${count} comments`;
      else if (count > 1) label = `${label} ×${count}`;
      parts.push({ kind, label });
    });
    return parts;
  };

  const cardColumns: TableColumn<CardGroup>[] = [
    {
      key: 'activity',
      label: 'Activity',
      render: (_v, row) => (
        <span style={{ display: 'inline-flex', gap: '6px', flexWrap: 'wrap' }}>
          {kindSummary(row).map(p => (
            <Badge key={p.kind} tone={KIND_TONES[p.kind]}>{p.label}</Badge>
          ))}
        </span>
      ),
    },
    {
      key: 'card',
      label: 'Card',
      render: (_v, row) => (
        <span>
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontWeight: 500,
              color: 'var(--text-body)',
            }}
          >
            <span title={row.cardName}>{excerpt(row.cardName, 40)}</span>
            <a
              href={row.cardUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
              className="row-chevron"
              title="Open in Trello"
            >
              <Icon name="external-link" size={12} />
            </a>
          </span>
          <span
            style={{ display: 'block', fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}
          >
            {excerpt(`${row.boardName}${row.listName ? ` · ${row.listName}` : ''}`, 48)}
          </span>
        </span>
      ),
    },
    {
      key: 'latest',
      label: 'Latest',
      render: (_v, row) => (
        <span style={{ display: 'block', maxWidth: '420px' }}>
          <span
            title={row.latest.body ? excerpt(row.latest.body, 300) : undefined}
            style={{ display: 'block', fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}
          >
            {row.latest.kind === 'card_created'
              ? `Created "${excerpt(row.latest.card_name, 32)}"`
              : excerpt(row.latest.body, 40) || '—'}
          </span>
          <span style={{ display: 'block', fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
            {row.latest.actor_name}
          </span>
        </span>
      ),
    },
    {
      key: 'age',
      label: 'Age',
      align: 'right',
      mono: true,
      render: (_v, row) => age(row.latest.occurred_at),
    },
    {
      key: 'actions',
      label: '',
      align: 'right',
      render: (_v, row) =>
        activeTab === 'open' ? (
          <Button
            size="sm"
            variant="secondary"
            onClick={e => {
              e?.stopPropagation?.();
              handleClearCard(row);
            }}
          >
            Clear
          </Button>
        ) : activeTab === 'assigned' ? (
          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)', whiteSpace: 'nowrap' }}>
            {row.task?.assigned_to_name || 'Unassigned'}
            {row.task?.due_date
              ? ` · due ${new Date(row.task.due_date).toLocaleDateString([], { month: 'short', day: 'numeric' })}`
              : ''}
          </span>
        ) : (
          <Button
            size="sm"
            variant="secondary"
            onClick={e => {
              e?.stopPropagation?.();
              handleReopenCard(row);
            }}
          >
            Reopen
          </Button>
        ),
    },
  ];

  // Feedback batches share the card columns: type chip, what and where, latest note, age, action.
  const feedbackCell = (key: string, b: FeedbackBatch, at: string): ReactNode => {
    const first = b.items.find(i => i.status !== 'withdrawn') ?? b.items[0];
    switch (key) {
      case 'activity':
        return (
          <span style={{ display: 'inline-flex', gap: '8px', alignItems: 'center' }}>
            <FeedbackPickBox feed={feed} batch={b} />
            <FeedbackTypeChip batch={b} />
          </span>
        );
      case 'card':
        return (
          <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <FeedbackThumb batch={b} size={34} />
            <span style={{ minWidth: 0 }}>
              <span style={{ display: 'block', fontWeight: 500, color: 'var(--text-body)' }}>
                {excerpt(`${b.client?.name ?? 'Client'} · ${b.items.length} note${b.items.length === 1 ? '' : 's'}`, 40)}
              </span>
              <FeedbackWhere batch={b} compact />
            </span>
          </span>
        );
      case 'latest':
        return (
          <span style={{ display: 'block', maxWidth: '420px' }}>
            <span title={first ? excerpt(first.message, 300) : undefined} style={{ display: 'block', fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>
              {first ? excerpt(first.message, 40) : '—'}
            </span>
            <span style={{ display: 'block', fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
              {b.sender?.full_name ?? 'Client'}{b.submission_note ? ' · note submitted' : ''}
            </span>
          </span>
        );
      case 'age':
        return age(at);
      case 'actions':
        return activeTab === 'assigned' ? (
          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)', whiteSpace: 'nowrap' }}>
            Agent queue{b.job?.status ? ` · ${b.job.status.replace(/_/g, ' ')}` : ''}
          </span>
        ) : (
          <Button
            size="sm"
            variant="secondary"
            onClick={e => {
              e?.stopPropagation?.();
              void feed.clear(b, activeTab === 'open');
            }}
          >
            {activeTab === 'open' ? 'Clear' : 'Reopen'}
          </Button>
        );
      default:
        return null;
    }
  };

  const columns: TableColumn<FeedRow>[] = cardColumns.map(col => ({
    ...col,
    render: (_v: unknown, row: FeedRow) =>
      row.type === 'feedback'
        ? feedbackCell(col.key, row.batch, row.at)
        : col.render
          ? col.render(row.group[col.key], row.group)
          : (row.group[col.key] as ReactNode),
  }));

  if (!isUserLoaded) return null;

  if (!hasAccess) {
    return <Navigate to="/clients" replace />;
  }

  return (
    <div className="min-h-screen">
      <Sidebar />

      <main className="px-4 md:px-8 pb-8 md:ml-[72px]">
        <PageHeader
          title="Client Comms"
          count={stats.openCards}
          searchPlaceholder="Search cards, boards, people..."
          searchValue={searchQuery}
          onSearchChange={setSearchQuery}
          actions={
            <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: '8px', marginLeft: '6px', alignItems: 'center' }}>
              <span title={connection.title} style={{ display: 'inline-flex' }}>
                <Badge tone={connection.tone} dot pulse={connection.pulse}>
                  {connection.label}
                </Badge>
              </span>
              <Button
                size="sm"
                variant="secondary"
                icon="search"
                title="Explore"
                aria-label="Explore"
                onClick={() => setIsExplorerOpen(true)}
              >
                {/* Icon-only on phones so the header takes one row, not three */}
                <span className="hidden sm:inline">Explore</span>
              </Button>
              <Button
                size="sm"
                variant="secondary"
                icon="settings"
                title="Boards"
                aria-label="Boards"
                onClick={() => setIsBoardsOpen(true)}
              >
                <span className="hidden sm:inline">Boards</span>
              </Button>
              <span className="hover-spin" style={{ display: 'inline-flex' }}>
                <Button
                  size="sm"
                  variant="secondary"
                  icon="refresh-cw"
                  disabled={isSyncing}
                  title="Sync Now"
                  aria-label="Sync Now"
                  onClick={handleSyncNow}
                >
                  <span className="hidden sm:inline">{isSyncing ? 'Syncing...' : 'Sync Now'}</span>
                </Button>
              </span>
            </span>
          }
        />

        {/* KPI strip */}
        <div
          className="ck-stagger grid grid-cols-2 lg:grid-cols-4"
          style={{ gap: 'var(--space-4)', marginBottom: 'var(--space-6)' }}
        >
          <StatCard
            label="Needs Attention"
            value={String(stats.openCards)}
            hint="cards with activity"
          />
          <StatCard
            label="Assigned"
            value={String(stats.assigned)}
            hint="turned into tasks"
          />
          <StatCard
            label="Comments"
            value={String(stats.comments)}
            hint="awaiting a reply"
          />
          <StatCard
            label="Oldest Waiting"
            value={stats.oldest ? age(stats.oldest) : '—'}
            hint={stats.oldest ? 'time to respond' : 'all clear'}
          />
        </div>

        {/* Tabs */}
        <div style={{ position: 'relative', marginBottom: 'var(--space-5)' }}>
          {activeTab === 'open' ? (
            <span style={{ position: 'absolute', right: 0, bottom: 6, zIndex: 1 }}>
              <FeedbackSelectToggle feed={feed} />
            </span>
          ) : null}
          <Tabs
            tabs={TAB_LABELS.map(t => t.label)}
            active={activeTabLabel}
            onChange={label => {
              const tab = TAB_LABELS.find(t => t.label === label);
              if (tab) setActiveTab(tab.value);
            }}
          />
        </div>

        {activeTab === 'open' ? <FeedbackMergeBar feed={feed} /> : null}
        {feed.error ? (
          <div style={{ marginBottom: 'var(--space-4)', fontSize: 'var(--text-sm)', color: 'var(--status-down)' }}>
            Client feedback didn’t load: {feed.error}
          </div>
        ) : null}

        {/* Table */}
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div
              className="w-7 h-7 rounded-full animate-spin"
              style={{ border: '2px solid var(--accent)', borderTopColor: 'transparent' }}
            />
          </div>
        ) : loadError ? (
          <div
            style={{
              background: 'var(--surface-card)',
              border: '1px solid var(--border-hairline)',
              borderRadius: 'var(--radius-lg)',
              padding: '48px 0',
              textAlign: 'center',
              color: 'var(--status-down)',
              fontSize: 'var(--text-sm)',
            }}
          >
            {loadError}
          </div>
        ) : filteredRows.length === 0 ? (
          <div
            style={{
              background: 'var(--surface-card)',
              border: '1px solid var(--border-hairline)',
              borderRadius: 'var(--radius-lg)',
              padding: '48px 0',
              textAlign: 'center',
              color: 'var(--text-faint)',
              fontSize: 'var(--text-sm)',
            }}
          >
            {activeTab === 'open'
              ? 'Inbox zero — nothing needs attention'
              : activeTab === 'assigned'
                ? 'Nothing assigned — convert an event to a task and it lands here'
                : 'Nothing cleared yet'}
          </div>
        ) : (
          <>
            <div className="hidden md:block overflow-x-auto">
              <Table
                columns={columns}
                rows={filteredRows}
                onRowClick={openRow}
                rowStyle={row =>
                  row.type === 'feedback' && feed.justMerged === row.batch.id
                    ? { boxShadow: 'inset 3px 0 0 var(--accent-text)', background: 'color-mix(in srgb, var(--accent-text) 7%, transparent)', transition: 'background 600ms var(--ease-out)' }
                    : undefined
                }
              />
            </div>
            {/* Phones: the five-column table was 1045px wide, so each card
                stacks into a tappable row instead */}
            <div
              className="md:hidden"
              style={{
                background: 'var(--surface-card)',
                border: '1px solid var(--border-hairline)',
                borderRadius: 'var(--radius-lg)',
                overflow: 'hidden',
              }}
            >
              {filteredRows.map((fr, i) => {
                const divider = i < filteredRows.length - 1 ? '1px solid var(--border-hairline)' : 'none';
                if (fr.type === 'feedback') {
                  const b = fr.batch;
                  const first = b.items.find(x => x.status !== 'withdrawn') ?? b.items[0];
                  return (
                    <div
                      key={fr.key}
                      role="button"
                      tabIndex={0}
                      data-ck-comms-row
                      className="hover-wash"
                      onClick={() => openRow(fr)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') openRow(fr);
                      }}
                      style={{ display: 'flex', flexDirection: 'column', gap: '6px', padding: '12px 14px', cursor: 'pointer', borderBottom: divider }}
                    >
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                        <span style={{ display: 'inline-flex', gap: '8px', alignItems: 'center', flex: 1, minWidth: 0, flexWrap: 'wrap' }}>
                          <FeedbackPickBox feed={feed} batch={b} />
                          <FeedbackTypeChip batch={b} />
                        </span>
                        <span className="t-num" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)', flexShrink: 0 }}>
                          {age(fr.at)}
                        </span>
                      </div>
                      <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <FeedbackThumb batch={b} size={40} />
                        <span style={{ minWidth: 0, flex: 1 }}>
                          <span style={{ display: 'block', fontWeight: 500, color: 'var(--text-body)' }}>
                            {b.client?.name ?? 'Client'} · {b.items.length} note{b.items.length === 1 ? '' : 's'}
                          </span>
                          <FeedbackWhere batch={b} compact />
                        </span>
                      </div>
                      <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', overflowWrap: 'anywhere' }}>
                        {b.sender?.full_name ?? 'Client'}: {first ? excerpt(first.message, 90) : '—'}
                      </span>
                    </div>
                  );
                }
                const row = fr.group;
                return (
                <div
                  key={fr.key}
                  role="button"
                  tabIndex={0}
                  data-ck-comms-row
                  className="hover-wash"
                  onClick={() => openRow(fr)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') openRow(fr);
                  }}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px',
                    padding: '12px 14px',
                    cursor: 'pointer',
                    borderBottom:
                      i < filteredRows.length - 1 ? '1px solid var(--border-hairline)' : 'none',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <span style={{ display: 'inline-flex', gap: '6px', flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
                      {kindSummary(row).map(p => (
                        <Badge key={p.kind} tone={KIND_TONES[p.kind]}>{p.label}</Badge>
                      ))}
                    </span>
                    <span
                      className="t-num"
                      style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)', flexShrink: 0 }}
                    >
                      {age(row.latest.occurred_at)}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                    <span
                      style={{
                        flex: 1,
                        minWidth: 0,
                        fontWeight: 500,
                        color: 'var(--text-body)',
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {row.cardName}
                    </span>
                    <a
                      href={row.cardUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={e => e.stopPropagation()}
                      title={row.isEmail ? 'Open in Gmail' : 'Open in Trello'}
                      aria-label={row.isEmail ? 'Open in Gmail' : 'Open in Trello'}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '36px',
                        height: '36px',
                        margin: '-8px -8px -8px 0',
                        flexShrink: 0,
                        color: 'var(--text-faint)',
                      }}
                    >
                      <Icon name="external-link" size={14} />
                    </a>
                  </div>
                  <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)', overflowWrap: 'anywhere' }}>
                    {row.boardName}
                    {row.listName ? ` · ${row.listName}` : ''}
                  </span>
                  <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', overflowWrap: 'anywhere' }}>
                    {row.latest.kind === 'card_created'
                      ? `Created by ${row.latest.actor_name}`
                      : `${row.latest.actor_name}: ${excerpt(row.latest.body, 90) || '—'}`}
                  </span>
                  {activeTab === 'assigned' ? (
                    <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
                      {row.task?.assigned_to_name || 'Unassigned'}
                      {row.task?.due_date
                        ? ` · due ${new Date(row.task.due_date).toLocaleDateString([], { month: 'short', day: 'numeric' })}`
                        : ''}
                    </span>
                  ) : (
                    <span style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={e => {
                          e?.stopPropagation?.();
                          if (activeTab === 'open') handleClearCard(row);
                          else handleReopenCard(row);
                        }}
                      >
                        {activeTab === 'open' ? 'Clear' : 'Reopen'}
                      </Button>
                    </span>
                  )}
                </div>
                );
              })}
            </div>
          </>
        )}
      </main>

      {/* Feedback batch side panel */}
      <SidePanel
        open={!!selectedFeedback}
        onClose={() => setSelectedFeedbackId(null)}
        eyebrow={selectedFeedback ? `Feedback · ${selectedFeedback.source?.label ?? 'app'}` : undefined}
        title={selectedFeedback ? `${selectedFeedback.client?.name ?? 'Client'} · ${selectedFeedback.items.length} note${selectedFeedback.items.length === 1 ? '' : 's'}` : undefined}
        width={widePanel ? 720 : 600}
        footer={selectedFeedback ? <FeedbackBatchActions feed={feed} batch={selectedFeedback} /> : undefined}
      >
        {selectedFeedback ? (
          <>
            {feedbackTab(selectedFeedback) === 'open' ? <FeedbackMergeBar feed={feed} /> : null}
            <FeedbackBatchDetail feed={feed} batch={selectedFeedback} />
          </>
        ) : null}
      </SidePanel>

      {/* Conversation side panel */}
      <SidePanel
        open={!!selectedGroup}
        onClose={() => setSelectedCardId(null)}
        eyebrow={
          selectedGroup
            ? selectedGroup.isEmail
              ? `Email · ${selectedGroup.items[0]?.actor_name ?? ''}`
              : `${selectedGroup.boardName}${selectedGroup.listName ? ` · ${selectedGroup.listName}` : ''}`
            : undefined
        }
        title={selectedGroup?.cardName}
        width={480}
        footer={
          selectedGroup ? (
            <span style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <a href={selectedGroup.cardUrl} target="_blank" rel="noopener noreferrer">
                <Button size="sm" variant="secondary" icon="external-link">
                  {selectedGroup.isEmail ? 'Open in Gmail' : 'Open in Trello'}
                </Button>
              </a>
              {isTaskActive(selectedGroup.task) ? null : selectedGroup.openCount > 0 ? (
                <Button size="sm" onClick={() => handleClearCard(selectedGroup)}>
                  Clear
                </Button>
              ) : (
                <Button size="sm" variant="secondary" onClick={() => handleReopenCard(selectedGroup)}>
                  Reopen
                </Button>
              )}
            </span>
          ) : undefined
        }
      >
        {selectedGroup && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            {/* An attached client must be changeable. Attaching the wrong one
                otherwise has no way back, which is the same trap Undo fixed on
                the Inbox side. allowClear so it can be detached entirely. */}
            {selectedGroup.isEmail && selectedGroup.clientId ? (
              <SearchableSelect
                label="Client"
                placeholder="Pick a client..."
                searchPlaceholder="Search clients..."
                options={clients.map(c => ({ value: c.id, label: c.name }))}
                value={selectedGroup.clientId}
                allowClear
                onChange={id => handleAttachClient(selectedGroup, id)}
                style={savingClient ? { opacity: 0.6 } : undefined}
              />
            ) : null}
            {/* Convert to task */}
            <div
              style={{
                background: 'var(--surface-raised)',
                border: '1px solid var(--border-hairline)',
                borderRadius: 'var(--radius-md)',
                padding: '12px 14px',
              }}
            >
              <div className="t-eyebrow" style={{ marginBottom: '8px' }}>Task</div>
              {linkedTask ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <button
                    type="button"
                    className="hover-wash pressable"
                    onClick={openLinkedTaskDetail}
                    title="Open task details"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      flex: 1,
                      minWidth: 0,
                      padding: '4px 6px',
                      margin: '-4px 0 -4px -6px',
                      border: 'none',
                      background: 'transparent',
                      borderRadius: 'var(--radius-sm)',
                      cursor: 'pointer',
                      textAlign: 'left',
                      fontFamily: 'var(--font-ui)',
                      fontSize: 'var(--text-sm)',
                      color: 'var(--text-body)',
                    }}
                  >
                    <Icon name="check" size={14} style={{ color: 'var(--status-up)', flexShrink: 0 }} />
                    <span style={{ flex: 1 }}>
                      Task exists{linkedTask.status ? ` · ${linkedTask.status}` : ''}
                      {linkedTask.assigned_to_name ? ` · ${linkedTask.assigned_to_name}` : ''}
                      {linkedTask.due_date
                        ? ` · due ${new Date(linkedTask.due_date).toLocaleDateString([], { month: 'short', day: 'numeric' })}`
                        : ''}
                    </span>
                    <Icon name="chevron-right" size={13} style={{ color: 'var(--text-faint)', flexShrink: 0 }} />
                  </button>
                  <button
                    type="button"
                    // p-3 on phones: the 22px desktop hit area is too small for a thumb
                    className="hover-wash pressable p-3 md:p-[5px]"
                    title="Detach this event from the task"
                    aria-label="Detach this event from the task"
                    onClick={() => handleDetach(selectedGroup)}
                    style={{
                      display: 'inline-flex',
                      border: 'none',
                      background: 'transparent',
                      borderRadius: 'var(--radius-sm)',
                      cursor: 'pointer',
                      color: 'var(--text-faint)',
                      flexShrink: 0,
                    }}
                  >
                    <Icon name="x" size={12} />
                  </button>
                </div>
              ) : selectedGroup.isEmail && !selectedGroup.clientId ? (
                // An email has no board, so "assign this board to a client" is
                // advice nobody can follow. Attach one right here instead.
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>
                    No client on this email yet. Attach one to create tasks from it.
                  </div>
                  <SearchableSelect
                    label="Client"
                    placeholder="Pick a client..."
                    searchPlaceholder="Search clients..."
                    options={clients.map(c => ({ value: c.id, label: c.name }))}
                    value={null}
                    onChange={id => handleAttachClient(selectedGroup, id)}
                  />
                </div>
              ) : !selectedGroup.clientId ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>
                    Assign this board to a client (Clients → Edit) to create tasks from its events.
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button size="sm" variant="secondary" icon="link" onClick={() => openAttachModal(selectedGroup)}>
                      Attach
                    </Button>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {/* minmax(0, 1fr): bare 1fr keeps the date input's min width and pushed
                      Assign To out of the box on a phone */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '8px' }}>
                    <Input
                      label="Due Date"
                      type="date"
                      value={taskDueDate}
                      onChange={e => setTaskDueDate(e.target.value)}
                    />
                    <Dropdown
                      label="Assign To"
                      placeholder="Pick a person..."
                      options={assignees.map(a => ({ value: a.id, label: a.name }))}
                      value={taskAssignee || ''}
                      onChange={e => setTaskAssignee(e.target.value || null)}
                    />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                    <Button size="sm" variant="secondary" icon="link" onClick={() => openAttachModal(selectedGroup)}>
                      Attach
                    </Button>
                    <Button
                      size="sm"
                      icon="plus"
                      disabled={!taskDueDate || !taskAssignee || isCreatingTask}
                      onClick={() => handleCreateTask(selectedGroup)}
                    >
                      {isCreatingTask ? 'Creating...' : 'Create Task'}
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Live Trello state: description, attachments, full comment history.
                An escalated email has no Trello card behind it, so asking Trello
                for one would 404 — the message body is in the activity list. */}
            {selectedGroup.isEmail ? null : (
              <TrelloCardDetailBody cardId={selectedGroup.cardId} />
            )}

            {/* Synced activity the inbox tracks — distinct from the Trello comment
                history above, since this is what "Clear" acts on */}
            <div className="t-eyebrow" style={{ marginTop: 'var(--space-2)' }}>
              Activity ({selectedGroup.items.length})
            </div>

            {selectedGroup.items.map(item => (
              <div
                key={item.id}
                style={{
                  background: item.direction === 'outbound' ? 'var(--accent-soft)' : 'var(--surface-card)',
                  border: '1px solid var(--border-hairline)',
                  borderRadius: 'var(--radius-md)',
                  padding: '12px 14px',
                  opacity: item.direction === 'inbound' && item.status === 'cleared' ? 0.55 : 1,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: '8px',
                    marginBottom: item.body ? '8px' : 0,
                  }}
                >
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: '8px',
                      fontWeight: 500,
                      fontSize: 'var(--text-sm)',
                      color: 'var(--text-body)',
                    }}
                  >
                    {item.actor_name}
                    {item.direction === 'outbound' ? (
                      <Badge tone="up">Reply</Badge>
                    ) : (
                      <Badge tone={KIND_TONES[item.kind]}>{KIND_LABELS[item.kind]}</Badge>
                    )}
                  </span>
                  <span
                    className="t-num"
                    style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}
                  >
                    {timeAgo(item.occurred_at)}
                  </span>
                </div>
                {item.body && (
                  <div
                    style={{
                      fontSize: 'var(--text-sm)',
                      color: 'var(--text-muted)',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {item.body}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </SidePanel>

      {/* Board explorer — browse a board and manually import cards */}
      <BoardExplorerPanel
        open={isExplorerOpen}
        onClose={() => setIsExplorerOpen(false)}
        boards={boards.filter(b => b.is_active)}
        presentCardIds={
          new Set([...tabGroups.open, ...tabGroups.assigned].map(g => g.cardId))
        }
        onImported={fetchItems}
      />

      {/* Boards panel — exclude boards from the inbox */}
      <SidePanel
        open={isBoardsOpen}
        onClose={() => setIsBoardsOpen(false)}
        eyebrow="Client Comms"
        title="Boards"
        width={420}
      >
        <div
          style={{
            fontSize: 'var(--text-xs)',
            color: 'var(--text-faint)',
            marginBottom: 'var(--space-4)',
          }}
        >
          Switched-off boards are not synced and their activity is hidden from the
          inbox. New Trello boards appear here automatically and start switched on.
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {boards.map(board => {
            const openCards = openCardsByBoard.get(board.id)?.size ?? 0;
            return (
              <div
                key={board.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '12px',
                  padding: '9px 2px',
                  borderBottom: '1px solid var(--border-hairline)',
                }}
              >
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: 'var(--text-sm)',
                    color: board.is_active ? 'var(--text-body)' : 'var(--text-faint)',
                    minWidth: 0,
                  }}
                >
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {board.board_name}
                  </span>
                  {board.is_active && openCards > 0 && (
                    <Badge tone="accent">{openCards}</Badge>
                  )}
                </span>
                <Switch
                  checked={board.is_active}
                  onChange={checked => handleToggleBoard(board, checked)}
                />
              </div>
            );
          })}
        </div>
      </SidePanel>

      {/* Linked task detail — opens over the conversation panel */}
      <TaskDetailSidepanel
        isOpen={isTaskDetailOpen}
        onClose={() => setIsTaskDetailOpen(false)}
        selectedTask={detailTask}
        users={assignees}
        onUpdateTask={handleTaskDetailUpdate}
        onArchiveTask={handleTaskDetailArchive}
      />

      {/* Attach event to an existing task */}
      <SidePanel
        open={isAttachOpen}
        onClose={() => setIsAttachOpen(false)}
        eyebrow={selectedGroup ? excerpt(selectedGroup.cardName, 48) : undefined}
        title="Attach to Task"
        width={420}
        footer={
          <>
            <Button
              icon="link"
              disabled={!attachTaskId || isAttaching}
              onClick={() => selectedGroup && attachTaskId && handleAttachToTask(selectedGroup, attachTaskId)}
            >
              {isAttaching ? 'Attaching...' : 'Attach'}
            </Button>
            <Button variant="ghost" onClick={() => setIsAttachOpen(false)} disabled={isAttaching}>
              Cancel
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {attachError ? (
            <div
              style={{
                padding: '10px 12px',
                borderRadius: 'var(--radius-md)',
                background: 'var(--status-down-soft)',
                color: 'var(--status-down)',
                fontSize: 'var(--text-sm)',
              }}
            >
              {attachError}
            </div>
          ) : null}
          <SearchableSelect
            label="Task"
            placeholder="Pick a task..."
            searchPlaceholder="Search tasks..."
            options={attachableTasks.map(t => ({
              value: t.id,
              label: t.title,
              hint: `${t.status}${t.assigned_to_name ? ` · ${t.assigned_to_name}` : ''}${
                t.due_date
                  ? ` · due ${new Date(t.due_date).toLocaleDateString([], { month: 'short', day: 'numeric' })}`
                  : ' · no due date'
              }`,
            }))}
            value={attachTaskId}
            onChange={setAttachTaskId}
          />
          <p style={{ margin: 0, fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
            {selectedGroup?.clientId
              ? "Showing this client's active tasks."
              : 'Showing all active tasks (this board has no client assigned).'}
            {' '}The event joins the task as a chip — the task keeps its own name,
            and submitting it clears every attached event.
          </p>
        </div>
      </SidePanel>
    </div>
  );
}
